// The onboarding routes (apps/api/src/routes/onboarding-admin.ts and onboarding-user.ts) and
// milestone 4's draft routes, as the portal calls them.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated, type Store } from '../api.js';
import type { Journey } from './journey.js';

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json().then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null)).catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}
const get = <T>(path: string) => fetch(path, { credentials: 'same-origin' }).then((r) => json<T>(r));
const post = <T>(path: string, body: unknown) =>
  fetch(path, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then((r) => json<T>(r));
const q = (store: string) => `store=${encodeURIComponent(store)}`;

/** CB5: the person's delegated Teams permission and completed onboarding offer. */
export interface TeamsConsentStatus {
  enabled: boolean;
  granted: boolean;
  storable: boolean;
  reason: string | null;
  offered: boolean;
  hadConsent: boolean;
}

export interface DirectoryUser { objectId: string; displayName: string; upn: string; mail: string | null; accountEnabled: boolean; userType: string | null; staff: boolean; inCobrain: boolean }
export interface SetupGroup { id: string; code: string; name: string }
export interface SetupPerson { principalId: string; displayName: string; upn: string; role: string; status: string; groupCode: string | null }
export interface Invite { to: string; subject: string; body: string; link: string }
export interface DraftSource { signalId: string; subject: string | null; occurredAt: string; fromName: string | null }
export interface Area { id: string; code: string; name: string; gist: string | null }
/** D219: the "Your own Claude" step. The address to paste into Claude, and the person's own live tokens (never a token itself). */
export interface YourOwnClaude {
  state: 'available';
  address: string;
  tokens: Array<{ id: string; label: string; createdAt: string; lastUsedAt: string | null; expiresAt: string | null }>;
  days: number;
  note: string;
}
/** A token just made: the one response that ever carries it (shown once, never stored readable). */
export interface MadeClaudeToken { address: string; token: { id: string; token: string; fingerprint: string; label: string; expiresAt: string | null } }
/**
 * Panel 2's ledger (U127, U137). `count` is null where nothing has come back from the other
 * system, and `note` then carries the words for it: there is no zero standing in for a silence.
 * `figure` says which round trip the number came from -- 'read' a backfill's running count,
 * 'answered' what a source said when Cobrain asked it.
 */
export interface HeldLine {
  kind: string;
  what: string;
  from: string;
  count: number | null;
  figure: 'read' | 'answered' | 'none';
  asAt: string | null;
  reading: boolean;
  note: string | null;
  askable: boolean;
}
export interface Held {
  since: string | null;
  lines: HeldLine[];
  notStarted: { kind: string; title: string; asked: boolean; count: number | null; expected: number | null } | null;
  cold: boolean;
}
/**
 * Panel 4 (U125): one thing Cobrain found while reading. There is deliberately no count on a
 * find -- the ledger above it is what counts, and a figure per row is a pass over the corpus per
 * row on a screen that re-reads every few seconds (apps/api/src/onboard/reading.ts).
 */
export interface Found { kind: 'company' | 'person' | 'meeting' | 'owed'; title: string; note: string | null; at: string }
/**
 * Panel 8 (U126): the closing figures. `owedToYou` counts what is owed to this person and says
 * nothing about whether it has gone quiet -- silence has no measured definition yet, and the last
 * screen of onboarding is the wrong place to invent one.
 */
export interface Ready {
  people: number; companies: number;
  owedByYou: number; owedToYou: number;
  earliestAt: string | null; readInMinutes: number | null;
}
/** What Fathom said when Cobrain asked it, as the card prints it. */
export interface ProbeView { outcome: string; count: number | null; earliestAt: string | null; latestAt: string | null; detail: string; askedAt: string }

export interface NewPersonBody { objectId: string; role: 'member' | 'admin'; groupId: string; managerPrincipalId: string | null; grants: string[]; privateStore: boolean; invite: true }

export interface ReadingBack { active: boolean; backTo: string; expected: number | null; done: number }
export interface WaitingRow {
  principalId: string; name: string; groupName: string | null; status: 'invited' | 'onboarding';
  steps: { invited: boolean; signedIn: boolean; privacyAccepted?: boolean; mailConnected: boolean; callsAnswered: boolean; draftsChecked: boolean; windowRead: boolean };
  privacyExempt?: boolean; privacyAcceptedAt?: string | null;
  stepsDone: number; corrections: number;
  holding: 'not_invited' | 'not_signed_in' | 'privacy' | 'mail' | 'calls' | 'reading' | 'drafts' | 'parked';
  since: string | null; readingStopped: boolean;
  /** Messages of their two weeks that failed twice and no longer hold their list (Mike, 10 Oct). */
  windowFailed?: number;
}
export interface NoFathom { actionId: string; principalId: string; name: string; declaredAt: string; askedAt: string | null }
export const onboarding = {
  teamsConsent: () => get<TeamsConsentStatus>('/api/teams-consent'),
  teamsConsentOffered: async (): Promise<void> => {
    const response = await fetch('/api/teams-consent/offered', { method: 'POST', credentials: 'same-origin' });
    if (!response.ok) await json<never>(response);
  },
  acceptPrivacy: (store: string, version: string) => post<{ acceptance: { version: string; acceptedAt: string }; journey: Journey }>('/api/onboarding/intro/privacy', { store, version, accepted: true }),
  granolaLater: (store: string) => post<{ journey: Journey }>('/api/onboarding/granola-later', { store }),
  noFathom: (store: string) => post<{ declaredAt: string; alerted?: number }>('/api/onboarding/no-fathom', { store }),
  readingBack: (store: string) => get<ReadingBack | null>(`/api/onboarding/reading-back?${q(store)}`),
  waiting: (store: string) => get<{ rows: WaitingRow[] }>(`/api/admin/onboarding/waiting?${q(store)}`),
  declarations: (store: string) => get<{ rows: NoFathom[] }>(`/api/admin/onboarding/no-fathom?${q(store)}`),
  markAsked: (store: string, id: string) => post<{ askedAt: string }>(`/api/admin/onboarding/no-fathom/${encodeURIComponent(id)}/asked`, { store }),
  granolaExport: (store: string, file: File) => fetch(`/api/onboarding/granola-export?${q(store)}&name=${encodeURIComponent(file.name)}`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/octet-stream' }, body: file,
  }).then((r) => json<{ journey: Journey; read: { detail?: string; note?: string } }>(r)),
  journey: (store: string) => get<{ store: Store; journey: Journey; yourOwnClaude: YourOwnClaude }>(`/api/onboarding/journey?${q(store)}`),
  claudeToken: (store: string) => post<MadeClaudeToken>('/api/onboarding/claude-token', { store }),
  connectMicrosoft: (store: string) => post<{ journey: Journey }>('/api/onboarding/connect-microsoft', { store }),
  held: (store: string) => get<{ held: Held; journey: Journey }>(`/api/onboarding/held?${q(store)}`),
  askFathom: (store: string) => post<{ probe: ProbeView | null; held: Held }>('/api/onboarding/ask-fathom', { store }),
  reading: (store: string) => get<{ held: Held; found: Found[] }>(`/api/onboarding/reading?${q(store)}`),
  ready: (store: string) => get<{ held: Held; ready: Ready }>(`/api/onboarding/ready?${q(store)}`),
  retry: (store: string) => post<{ retried: string[]; journey: Journey }>('/api/onboarding/retry', { store }),
  notetaker: (store: string, kind: 'fathom' | 'granola', account: string) => post<{ journey: Journey }>('/api/onboarding/notetaker', { store, kind, account }),
  optional: (store: string, kind: 'jira' | 'slack') => post<{ journey: Journey }>('/api/onboarding/optional', { store, kind }),
  draftSources: (store: string) => get<{ sources: DraftSource[] }>(`/api/onboarding/draft-sources?${q(store)}`),
  areas: (store: string) => get<{ areas: Area[] }>(`/api/onboarding/areas?${q(store)}`),
  addArea: (store: string, name: string) => post<{ area: Area }>('/api/onboarding/areas', { store, name }),
  stage: (store: string, stage: 'areas_done' | 'done') => post<{ journey: Journey }>('/api/onboarding/stage', { store, stage }),

  setup: (store: string) => get<{ groups: SetupGroup[]; people: SetupPerson[]; policyPublished: boolean }>(`/api/admin/onboarding/setup?${q(store)}`),
  directory: (store: string, text: string) => get<{ users: DirectoryUser[] }>(`/api/admin/onboarding/directory?${q(store)}&q=${encodeURIComponent(text)}`),
  // R58: creating somebody also joins them to their own contact record, and the answer says
  // which it did so the panel can say it rather than the admin having to go and look.
  createPerson: (store: string, body: NewPersonBody) => post<{
    invited: true; inviteText: string;
    person: { principalId: string; upn: string; displayName: string; status: string };
    join: { outcome: string; personId: string | null; note: string | null };
  }>('/api/admin/onboarding/people', { store, ...body }),
  invite: (store: string, principalId: string) => post<{ invite: Invite; mailSent: false }>(`/api/admin/onboarding/people/${encodeURIComponent(principalId)}/invite`, { store }),
};
// The drafts themselves go through milestone 4's `drafts` client in ../api.js (create, edit); never send from onboarding.
